import type { TerraSignalRow } from '@shared/schemas/terra-signals';
import { SALESFORCE_SYNC_ACTOR, type SIGNAL_ACTION_STATUS_ORDER } from '@shared/schemas/terra-signals';
import { fmtDateOnlyText } from '../account/accountFieldFormat';

const SHORT_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

/**
 * The expanded-detail field list for an ACTION row (`terra_signals`), shared
 * by both timelines' accordions.
 *
 * Lives here rather than in either component (U6/D9): `ContactTimeline.tsx`
 * was its only producer, and `AccountTimeline.tsx` now renders the same
 * expanded body for its merged action rows — importing one component's helper
 * from a sibling component would be a component-to-component import, so the
 * helper moved down into this shared `actions/` module (alongside
 * `ActionsTabList.tsx`, the other `TerraSignalRow`-shaped renderer) instead.
 */

export interface DetailField { label: string; value: string }

/** `signal_status` values meaning the underlying flow/send is still actively
 *  executing — see `isLiveActionRow` below for why this is checked separately
 *  from `action_state`. */
const LIVE_SIGNAL_STATUSES: readonly string[] = ['Running', 'Pending'] as const satisfies readonly (typeof SIGNAL_ACTION_STATUS_ORDER)[number][];

/**
 * Whether an action row is "live/open" — the rule behind both timelines' light
 * orange row tint (Spencer, 2026-09-22: "the entire row should highlight
 * orange if there's a recent or live terra action").
 *
 * Checks TWO separate fields, not one (2026-09-23 fix, Spencer QA: a row whose
 * name said "... Running" wasn't highlighted): `action_state`
 * (Completed/Archived/Expired/To do/Insight/Snoozed) is whether the SALESFORCE
 * RECORD is closed — `signal_status` (Running/Pending/Paused/Finished/No flow/
 * etc., per-arm vocabulary, see `signal-action.ts`'s `STATUS_CASE`) is whether
 * the underlying GONG FLOW is still executing. These are independent: a rep can
 * close the Salesforce record (`action_state: 'Completed'`) while the flow send
 * is still running (`signal_status: 'Running'`) — that row IS live.
 *
 * Shared by `ContactTimeline` and `AccountTimeline` so the two can never drift
 * into tinting different rows (U6/D9).
 */
export function isLiveActionRow(row: TerraSignalRow): boolean {
  return (
    !['Completed', 'Archived', 'Expired'].includes(row.action_state)
    || (!!row.signal_status && LIVE_SIGNAL_STATUSES.includes(row.signal_status))
  );
}

/** Build a label/value list, skipping empties. */
export function fields(...pairs: [string, string | null | undefined][]): DetailField[] {
  return pairs
    .filter((p): p is [string, string] => !!p[1] && p[1].trim() !== '')
    .map(([label, value]) => ({ label, value }));
}

/** ISO date → short "Sep 15, 3:04 PM" — used for "Actioned at", where the
 *  time (not just the day) is the point of the field. */
function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * Expanded-detail fields for an `action` entry — answers "assigned to whom,
 * what name/type/subtype, who actioned it and when" (Spencer, 2026-09-22).
 * `SALESFORCE_SYNC_ACTOR` is never rep-facing (matches the Plays People
 * table's `signal_completed_by` renderer convention).
 */
export function actionFields(row: TerraSignalRow): DetailField[] {
  const actionedBy = row.action_completed_by_name
    ? (row.action_completed_by_name === SALESFORCE_SYNC_ACTOR ? 'Unknown (actioned outside Terra)' : row.action_completed_by_name)
    : null;
  return fields(
    ['Name', row.signal_name],
    ['Assigned to', row.assignee],
    ['Type', row.signal_type],
    ['Subtype', row.signal_subtype],
    ['Status', row.action_state ?? row.signal_status],
    ['Actioned by', actionedBy],
    ['Actioned at', row.action_completed_at ? fmtDateTime(row.action_completed_at) : null],
    ['Due', fmtDateOnlyText(row.due_date, SHORT_DAY)],
    ['Source', row.source_system],
  );
}
